/**
 * Auth context + runtime theming.
 *
 * The tenant theme arrives with the session and is applied as CSS custom
 * properties, so one build serves every tenant with no branding baked in (§0.1).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, onSessionExpired, tokens } from './api';

export type Role =
  | 'SUPERADMIN'
  | 'MEDICAL_DIRECTOR'
  | 'ADMIN'
  | 'CLINICIAN'
  | 'STAFF';

/** The two cross-tenant platform roles: billing owner and medical director. */
export const PLATFORM_ROLES: Role[] = ['SUPERADMIN', 'MEDICAL_DIRECTOR'];

export interface SessionTenant {
  id: string;
  name: string;
  slug: string;
  inbodyEnabled: boolean;
  primaryColor: string;
  accentColor: string;
  neutralBg: string;
  wordmark: string | null;
  wordmarkSub: string | null;
  brandWord: string | null;
  logoUrl: string | null;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  lastLoginAt: string | null;
  tenant: SessionTenant;
}

interface AuthState {
  user: SessionUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login(email: string, password: string, tenantSlug?: string): Promise<void>;
  /** Self-serve provider sign-up (Q1). Signs the new provider straight in. */
  signUp(input: SignUpInput): Promise<void>;
  logout(): Promise<void>;
  refreshUser(): Promise<void>;
  /** True when the user's role is in `allowed`. */
  can(...allowed: Role[]): boolean;
}

export interface SignUpInput {
  providerType: 'DOCTOR' | 'NURSE';
  legalFirstName: string;
  legalLastName: string;
  credentials: string;
  practiceName: string;
  email: string;
  phone: string;
  password: string;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [status, setStatus] = useState<AuthState['status']>('loading');

  const loadUser = useCallback(async () => {
    if (!tokens.access) {
      setUser(null);
      setStatus('anonymous');
      return;
    }
    try {
      const me = await api.get<SessionUser>('/auth/me');
      setUser(me);
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  // A failed refresh anywhere in the app drops us back to anonymous.
  useEffect(
    () =>
      onSessionExpired(() => {
        setUser(null);
        setStatus('anonymous');
      }),
    [],
  );

  // Apply the tenant theme to the document root.
  useEffect(() => {
    applyTheme(user?.tenant ?? null);
  }, [user]);

  const login = useCallback(
    async (email: string, password: string, tenantSlug?: string) => {
      const result = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: SessionUser;
      }>('/auth/login', { email, password, tenantSlug }, { anonymous: true });

      tokens.set(result.accessToken, result.refreshToken);
      // Re-read from /me so the session shape is identical on login and reload.
      await loadUser();
    },
    [loadUser],
  );

  const signUp = useCallback(
    async (input: SignUpInput) => {
      const result = await api.post<{
        accessToken: string;
        refreshToken: string;
        user: SessionUser;
      }>('/auth/signup', input, { anonymous: true });

      tokens.set(result.accessToken, result.refreshToken);
      await loadUser();
    },
    [loadUser],
  );

  const logout = useCallback(async () => {
    const refreshToken = tokens.refresh;
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch {
      // Logging out locally matters more than the server acknowledging it.
    }
    tokens.clear();
    setUser(null);
    setStatus('anonymous');
  }, []);

  const can = useCallback(
    (...allowed: Role[]) => (user ? allowed.includes(user.role) : false),
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, status, login, signUp, logout, refreshUser: loadUser, can }),
    [user, status, login, signUp, logout, loadUser, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/* ------------------------------------------------------------------ *
 * Theming
 * ------------------------------------------------------------------ */

/** The neutral, unbranded default (§0.1). */
const NEUTRAL = {
  primary: '#1B2430',
  accent: '#2F5D8A',
  bg: '#F7F8FA',
  ink: '#1B2430',
};

const BANDS = {
  optimal: '#42954D',
  warn: '#F8BC28',
  risk: '#D43C2F',
};

function applyTheme(tenant: SessionTenant | null): void {
  const root = document.documentElement;

  root.style.setProperty('--brand-primary', tenant?.primaryColor ?? NEUTRAL.primary);
  // The report's tan accent is too low-contrast for UI affordances like buttons
  // and focus rings, so the app keeps a dedicated UI accent and uses the tenant
  // accent for brand furniture only. Under V3 that dedicated accent is Steel,
  // the design system's single action colour.
  root.style.setProperty('--brand-accent', NEUTRAL.accent);
  root.style.setProperty('--brand-tenant-accent', tenant?.accentColor ?? NEUTRAL.accent);
  root.style.setProperty('--brand-bg', NEUTRAL.bg);
  root.style.setProperty('--brand-ink', NEUTRAL.ink);

  root.style.setProperty('--band-optimal', BANDS.optimal);
  root.style.setProperty('--band-warn', BANDS.warn);
  root.style.setProperty('--band-risk', BANDS.risk);

  const themeColor = document.querySelector('meta[name="theme-color"]');
  themeColor?.setAttribute('content', tenant?.primaryColor ?? NEUTRAL.primary);
}
